import { useCallback, useEffect, useState } from "react";
import { Button, Form, Input, Modal, Select, Space, Switch, Table, Tag, message } from "antd";
import PageTitle from "../../components/PageTitle";
import {
  createStaffMeterSupplement,
  deleteStaffMeterSupplement,
  getStaffMeterSupplementList,
  getUsers,
  patchStaffMeterSupplement,
} from "../../api";
import { formatAdminUserSelectLabel } from "../../utils/userDisplay";

function extractUsers(res) {
  if (!res?.success) return [];
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.data?.list)) return res.data.list;
  if (Array.isArray(res.list)) return res.list;
  return [];
}

const userTypeLabel = (t) => {
  const n = Number(t);
  if (n === 1) return "房东";
  if (n === 2) return "租客";
  if (n === 3) return "管理员";
  return `类型${t ?? "-"}`;
};

/** 业务员名单：只加人，不改 user.user_type，不展示月限额。 */
const StaffMeterSupplement = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getStaffMeterSupplementList();
      if (res?.success) setData(Array.isArray(res.data) ? res.data : []);
      else message.error(res?.error || "加载失败");
    } catch {
      message.error("加载业务员名单失败");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const searchUsers = async () => {
    const kw = String(keyword || "").trim();
    if (!kw) {
      message.warning("请输入手机号或昵称关键词");
      return;
    }
    setSearching(true);
    try {
      const res = await getUsers({ keyword: kw, page: 1, page_size: 20 });
      const list = extractUsers(res);
      setCandidates(list);
      if (!list.length) message.info("未找到用户");
    } catch {
      message.error("搜索用户失败");
    } finally {
      setSearching(false);
    }
  };

  const handleAdd = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      const res = await createStaffMeterSupplement({
        user_id: Number(values.user_id),
      });
      if (!res?.success) throw new Error(res?.error || "添加失败");
      message.success("已加入业务员名单");
      setModalOpen(false);
      form.resetFields();
      setCandidates([]);
      setKeyword("");
      load();
    } catch (e) {
      message.error(e?.message || "添加失败");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (row, checked) => {
    try {
      const res = await patchStaffMeterSupplement(row.id, { status: checked ? 1 : 0 });
      if (!res?.success) throw new Error(res?.error || "更新失败");
      message.success(checked ? "已启用" : "已停用");
      load();
    } catch (e) {
      message.error(e?.message || "更新失败");
    }
  };

  const handleRemove = (row) => {
    Modal.confirm({
      title: `移出「${row.nickname || row.user_id}」？`,
      content: "移出后该用户不再是业务员。不影响其房东/租客身份。",
      onOk: async () => {
        const res = await deleteStaffMeterSupplement(row.id);
        if (res?.success) {
          message.success("已移出");
          load();
        } else {
          message.error(res?.error || "移出失败");
        }
      },
    });
  };

  const columns = [
    { title: "ID", dataIndex: "id", width: 70 },
    { title: "用户ID", dataIndex: "user_id", width: 90 },
    {
      title: "昵称",
      dataIndex: "nickname",
      render: (v) => v || "—",
    },
    {
      title: "手机",
      dataIndex: "phone",
      width: 130,
      render: (v) => v || "—",
    },
    {
      title: "基础身份",
      dataIndex: "user_type",
      width: 90,
      render: (t) => <Tag>{userTypeLabel(t)}</Tag>,
    },
    {
      title: "启用",
      dataIndex: "status",
      width: 80,
      render: (s, row) => (
        <Switch checked={Number(s) === 1} onChange={(c) => handleToggleStatus(row, c)} />
      ),
    },
    {
      title: "操作",
      width: 80,
      render: (_, row) => (
        <Button type="link" size="small" danger onClick={() => handleRemove(row)}>
          移出
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageTitle title="业务员名单" />
      <div style={{ marginBottom: 12, color: "#666", fontSize: 13 }}>
        只标记谁是业务员，不改原有房东/租客身份。目前没有额外权限。
      </div>
      <div style={{ marginBottom: 12 }}>
        <Button
          type="primary"
          onClick={() => {
            form.setFieldsValue({ user_id: undefined });
            setModalOpen(true);
          }}
        >
          加入业务员
        </Button>
        <Button style={{ marginLeft: 8 }} onClick={load}>
          刷新
        </Button>
      </div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        size="small"
        pagination={false}
      />

      <Modal
        title="加入业务员"
        open={modalOpen}
        onOk={handleAdd}
        confirmLoading={submitting}
        onCancel={() => setModalOpen(false)}
        destroyOnClose
      >
        <Space style={{ marginBottom: 12 }} wrap>
          <Input
            placeholder="手机号/昵称搜索"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={searchUsers}
            style={{ width: 220 }}
          />
          <Button loading={searching} onClick={searchUsers}>
            搜索用户
          </Button>
        </Space>
        <Form form={form} layout="vertical">
          <Form.Item
            name="user_id"
            label="选择用户"
            rules={[{ required: true, message: "请选择用户" }]}
          >
            <Select
              placeholder="先搜索再选择"
              showSearch
              optionFilterProp="label"
              options={candidates.map((u) => ({
                value: u.id,
                label: formatAdminUserSelectLabel
                  ? formatAdminUserSelectLabel(u)
                  : `${u.nickname || "未命名"} / ${u.phone || "无手机"} (ID:${u.id})`,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StaffMeterSupplement;
